import { useState, useEffect } from "react";
import api from "api/axios";
import { handleAxiosAndCaughtError } from "utils";
import { Conversation } from "../index";
import { GenericAbortSignal } from "axios";

const useConversations = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [totalConversations, setTotalConversations] = useState<number | null>(
    null
  );
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [conversationsError, setConversationsError] = useState<string | null>(
    null
  );

  const fetchConversations = async (signal?: GenericAbortSignal) => {
    setIsLoadingConversations(true);
    setConversationsError(null);
    try {
      const response = await api.get("/conversations", {
        params: { take: 20, skip: 0 },
        ...(signal instanceof AbortSignal ? { signal } : {}),
      });
      setConversations(response.data.items);
      setTotalConversations(response.data.total);
    } catch (err) {
      // @ts-expect-error: no prob
      if (err?.name === "CanceledError" || signal?.aborted) return;
      setConversationsError("Failed to load conversations. Please try again.");
      handleAxiosAndCaughtError(err);
    } finally {
      setIsLoadingConversations(false);
    }
  };

  const handleLoadMoreConversations = async () => {
    const abortController = new AbortController();
    try {
      const response = await api.get("/conversations", {
        params: { take: 10, skip: conversations.length },
        signal: abortController.signal,
      });

      if (response.data.items.length > 0) {
        setConversations((prevConversations) => [
          ...prevConversations,
          ...response.data.items,
        ]);
      }
    } catch (err) {
      // @ts-expect-error: no prob
      if (err.name === "CanceledError" || abortController.signal.aborted)
        return;
      setConversationsError(
        "Failed to load more conversations. Please try again."
      );
      handleAxiosAndCaughtError(err);
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchConversations(abortController.signal);
    return () => abortController.abort();
  }, []);

  return {
    conversations,
    totalConversations,
    isLoadingConversations,
    conversationsError,
    fetchConversations,
    handleLoadMoreConversations,
    setConversationsError,
  };
};

export default useConversations;
